import React, { useState, useMemo, useEffect } from "react";
import AppLayout from "layouts/AppLayout";
import FilterToolbar from "./components/FilterToolbar";
import MobileFilterPanel from "./components/MobileFilterPanel";
import OpportunityGrid from "./components/OpportunityGrid";
import SavedSearchesBar from "./components/SavedSearchesBar";
import CompareBar from "./components/CompareBar";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import OpportunityCard from "./components/OpportunityCard";
import { MARKETPLACE_STATES } from "config/workflowStates";
import { useRole } from "context/RoleContext";

const OPPORTUNITIES = [
{
  id: 1,
  name: "Nordic Fibre Network — Phase II Expansion",
  sector: "Digital Infrastructure",
  region: "Sweden",
  stage: "Diligence",
  targetIRR: 15.2,
  ticketSize: "£10M–£30M",
  equitySought: "£41M",
  riskRating: "Low",
  impactThemes: ["Digital Access", "Social Infrastructure"],
  description: "Follow-on expansion of the operational Nordic Fibre Network asset. Phase II extends fibre coverage across Sweden, targeting 180,000 additional premises with long-term anchor contracts in place.",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a322fc8f-1766858585008.png",
  imageAlt: "Fibre optic cables glowing with blue light in a data centre server room with rows of equipment",
  closingDate: "30/09/2026",
  sponsor: "Nordic Digital Infrastructure Partners",
  marketplaceState: MARKETPLACE_STATES.DD_ACCESS,
},
{
  id: 2,
  name: "Avon Water Treatment Works — Phase 2",
  sector: "Utilities",
  region: "UK",
  stage: "Diligence",
  targetIRR: 9.1,
  ticketSize: "£20M–£60M",
  equitySought: "£48M",
  riskRating: "Low",
  impactThemes: ["Water Security", "Social Infrastructure"],
  description: "Phase 2 capacity expansion of the operational Avon Water Treatment Works, serving an additional 80,000 households in the South West of England under the AMP8 regulatory framework.",
  image: "https://images.unsplash.com/photo-1622322977879-af6982dcba00",
  imageAlt: "Large circular water treatment facility with blue water and industrial processing equipment from aerial view",
  closingDate: "28/02/2027",
  sponsor: "Aqua Capital Partners",
  marketplaceState: MARKETPLACE_STATES.LISTED,
},
{
  id: 3,
  name: "Iberian Battery Storage Complex",
  sector: "Renewable Energy",
  region: "Spain",
  stage: "Sourcing",
  targetIRR: 13.8,
  ticketSize: "£15M–£80M",
  equitySought: "£90M",
  riskRating: "Medium",
  impactThemes: ["Clean Energy", "Climate"],
  description: "Large-scale battery energy storage complex in Spain, co-located with existing solar assets. Provides grid balancing services under a 15-year capacity agreement with REE.",
  image: "https://images.unsplash.com/photo-1698508629476-cd445c7d70ac",
  imageAlt: "Large offshore wind turbines standing in calm sea water during golden hour with clear blue sky",
  closingDate: "31/12/2026",
  sponsor: "Iberian Energy Storage SARL",
  marketplaceState: MARKETPLACE_STATES.NDA_PENDING,
},
{
  id: 4,
  name: "Elbe Biomass Plant — Capacity Extension",
  sector: "Renewable Energy",
  region: "Germany",
  stage: "Screening",
  targetIRR: 11.8,
  ticketSize: "£10M–£40M",
  equitySought: "£57M",
  riskRating: "Medium",
  impactThemes: ["Clean Energy", "Climate"],
  description: "Capacity extension of the Elbe Biomass Plant currently under construction in Germany. Extension adds 30MW of additional generation capacity with a 20-year offtake agreement.",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_130e15fee-1769351129216.png",
  imageAlt: "Vast solar panel farm stretching across dry Spanish landscape with mountains visible in background",
  closingDate: "30/06/2027",
  sponsor: "Elbe Energie GmbH",
  marketplaceState: MARKETPLACE_STATES.COMMITTED,
},
{
  id: 5,
  name: "Lisbon Metro Extension — Line 4",
  sector: "Transport",
  region: "Portugal",
  stage: "Screening",
  targetIRR: 10.5,
  ticketSize: "£25M–£75M",
  equitySought: "£94M",
  riskRating: "Low",
  impactThemes: ["Mobility", "Climate"],
  description: "Extension of the Lisbon Metro network with a new Line 4 connecting the eastern suburbs to the city centre. 30-year concession backed by the Portuguese government.",
  image: "https://images.unsplash.com/photo-1606723662343-07e078053dc5",
  imageAlt: "Modern rail freight terminal with multiple tracks and large warehouse facilities under overcast sky",
  closingDate: "31/03/2027",
  sponsor: "Lisbon Metro Consortium",
  marketplaceState: MARKETPLACE_STATES.ACCESS_REQUESTED,
},
{
  id: 6,
  name: "East Anglia Carbon Capture",
  sector: "Energy Transition",
  region: "UK",
  stage: "Sourcing",
  targetIRR: 14.2,
  ticketSize: "£10M–£50M",
  equitySought: "£75M",
  riskRating: "High",
  impactThemes: ["Climate", "Energy Transition"],
  description: "Carbon capture and storage facility in East Anglia targeting industrial emitters. Early-stage prospect with government feasibility funding secured. CCS cluster designation pending.",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_18732bc97-1772159737416.png",
  imageAlt: "Industrial facility with large pipes and carbon capture equipment against a clear sky",
  closingDate: "TBC",
  sponsor: "East Anglia CCS Consortium",
  marketplaceState: MARKETPLACE_STATES.LISTED,
}];



const DEFAULT_FILTERS = {
  sector: "All Sectors",
  region: "All Regions",
  stage: "All Stages",
  riskRating: "All",
  impactThemes: [],
  irrMin: 0,
  irrMax: 30,
  search: ""
};

export default function OpportunitiesListPage() {
  const { role, isExternal } = useRole();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [sortBy, setSortBy] = useState("relevance");
  const [bookmarks, setBookmarks] = useState([1, 3]);
  const [stateOverrides, setStateOverrides] = useState({});
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [compareList, setCompareList] = useState([]);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setIsLoading(false), 800);
    return () => clearTimeout(t);
  }, []);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleRequestAccess = (id) => {
    setStateOverrides((prev) => ({ ...prev, [id]: MARKETPLACE_STATES.ACCESS_REQUESTED }));
    showToast("Access request submitted — the deal team will be in touch.");
  };

  const handleBookmarkToggle = (id) => {
    setBookmarks((prev) => {
      const updated = prev?.includes(id) ? prev?.filter((b) => b !== id) : [...prev, id];
      showToast(prev?.includes(id) ? "Bookmark removed" : "Opportunity bookmarked");
      return updated;
    });
  };

  const filteredAndSorted = useMemo(() => {
    let result = OPPORTUNITIES?.filter((opp) => {
      if (filters?.sector !== "All Sectors" && opp?.sector !== filters?.sector) return false;
      if (filters?.region !== "All Regions" && opp?.region !== filters?.region) return false;
      if (filters?.stage !== "All Stages" && opp?.stage !== filters?.stage) return false;
      if (filters?.riskRating !== "All" && opp?.riskRating !== filters?.riskRating) return false;
      if (opp?.targetIRR < filters?.irrMin || opp?.targetIRR > filters?.irrMax) return false;
      if (filters?.impactThemes?.length > 0 && !filters?.impactThemes?.some((t) => opp?.impactThemes?.includes(t))) return false;
      if (filters?.search) {
        const q = filters?.search?.toLowerCase();
        if (!opp?.name?.toLowerCase()?.includes(q) && !opp?.description?.toLowerCase()?.includes(q) && !opp?.sector?.toLowerCase()?.includes(q)) return false;
      }
      return true;
    });

    // External roles (co_investor, external_investor, deal_originator) see only LISTED opportunities
    if (isExternal) {
      result = result.filter((opp) => opp?.marketplaceState === MARKETPLACE_STATES.LISTED);
    }

    switch (sortBy) {
      case "irr_desc":result = [...result]?.sort((a, b) => b?.targetIRR - a?.targetIRR);break;
      case "irr_asc":result = [...result]?.sort((a, b) => a?.targetIRR - b?.targetIRR);break;
      case "stage":result = [...result]?.sort((a, b) => a?.stage?.localeCompare(b?.stage));break;
      default:break;
    }
    return result;
  }, [filters, sortBy, isExternal]);

  return (
    <AppLayout>
      <div className="min-h-screen bg-background">
        <div className="px-6 md:px-10 lg:px-12 py-8">
          {/* Page header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="font-heading font-semibold text-foreground text-2xl md:text-3xl">
                Opportunities
              </h1>
              <p className="text-muted-foreground font-body text-sm mt-1">
                Discover and evaluate infrastructure equity deals across sectors and regions.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                iconName="Bell"
                iconPosition="left"
                iconSize={16}
                onClick={() => showToast("Deal alert preferences saved")}>
                
                Deal Alerts
              </Button>
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                iconSize={16}
                onClick={() => showToast("Submit opportunity form opened")}>
                
                Submit Deal
              </Button>
            </div>
          </div>

          {/* Saved searches */}
          <div className="mb-4">
            <SavedSearchesBar onApply={(s) => showToast(`Applied: ${s?.label}`)} />
          </div>

          {/* Filter toolbar */}
          <div className="mb-6">
            <FilterToolbar
              filters={filters}
              onFilterChange={setFilters}
              onSortChange={setSortBy}
              sortBy={sortBy}
              resultCount={filteredAndSorted?.length}
              onMobileFilterToggle={() => setMobileFilterOpen(true)} />
            
          </div>

          {/* Bookmarked section */}
          {bookmarks?.length > 0 &&
          <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Icon name="Bookmark" size={16} color="var(--color-primary)" />
                <h2 className="font-heading font-semibold text-foreground text-base">Bookmarked Deals</h2>
                <span className="text-xs font-caption text-muted-foreground">({bookmarks?.length})</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {OPPORTUNITIES?.filter((o) => bookmarks?.includes(o?.id))?.map((opp) =>
              <OpportunityCard
                key={opp?.id}
                opportunity={opp}
                isBookmarked={true}
                isExternal={isExternal}
                onBookmarkToggle={handleBookmarkToggle}
                stateOverrides={stateOverrides}
                onRequestAccess={handleRequestAccess} />
              )}
              </div>
            </div>
          }

          {/* Main grid */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-foreground text-base">
                All Opportunities
              </h2>
              <div className="flex items-center gap-2">
                <span className="text-xs font-caption text-muted-foreground">
                  {filteredAndSorted?.length} results
                </span>
              </div>
            </div>
            <OpportunityGrid
              opportunities={filteredAndSorted}
              bookmarks={bookmarks}
              onBookmarkToggle={handleBookmarkToggle}
              isLoading={isLoading}
              isExternal={isExternal}
              stateOverrides={stateOverrides}
              onRequestAccess={handleRequestAccess} />
            
          </div>

          {/* Load more */}
          {!isLoading && filteredAndSorted?.length > 0 &&
          <div className="flex justify-center mt-10">
              <Button
              variant="outline"
              iconName="RefreshCw"
              iconPosition="left"
              iconSize={16}
              onClick={() => showToast("All opportunities loaded")}>
              
                Load More Opportunities
              </Button>
            </div>
          }
        </div>
      </div>
      {/* Mobile filter panel */}
      <MobileFilterPanel
        isOpen={mobileFilterOpen}
        onClose={() => setMobileFilterOpen(false)}
        filters={filters}
        onFilterChange={(f) => {setFilters(f);showToast("Filters applied");}} />
      
      {/* Compare bar */}
      <CompareBar
        selectedCount={compareList?.length}
        onClear={() => setCompareList([])}
        onCompare={() => showToast("Comparison view coming soon")} />
      
      {/* Toast */}
      {toast &&
      <div
        className="fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-sm font-caption font-medium text-white transition-all duration-300"
        style={{ background: toast?.type === "error" ? "var(--color-error)" : "var(--color-primary)", minWidth: "220px" }}>
        
          <Icon name={toast?.type === "error" ? "AlertCircle" : "CheckCircle"} size={18} color="white" />
          {toast?.message}
        </div>
      }
    </AppLayout>);

}